import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Centralised SEO helper for HelloSir.ai marketing pages.
 *  - Adds title / description / canonical
 *  - Open Graph + Twitter cards
 *  - Optional JSON-LD structured data
 *  - hreflang x-default for global reach
 */
const SITE = "https://hellosir.ai";
const BRAND = "HelloSir.ai";
const DEFAULT_OG = "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=1200&q=80";

export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_OG,
  keywords = [],
  jsonLd = null,
  noIndex = false,
}) {
  const url = `${SITE}${path}`;
  const fullTitle = title?.includes(BRAND) ? title : `${title} | ${BRAND}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Global reach */}
      <link rel="alternate" hrefLang="x-default" href={url} />
      <link rel="alternate" hrefLang="en" href={url} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export const ORG_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: BRAND,
  url: SITE,
  logo: `${SITE}/logo.png`,
  description:
    "HelloSir.ai is an AI communication platform offering text chatbots, voice chatbots, and AI calling agents for businesses worldwide.",
  parentOrganization: {
    "@type": "Organization",
    name: "Cloud Lab Technologies",
    url: "https://www.cloudlabweb.com",
    address: {
      "@type": "PostalAddress",
      addressCountry: "GB",
    },
  },
  sameAs: ["https://www.cloudlabweb.com"],
};
